import React, { useLayoutEffect } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDonate } from '../../contexts/PublicDonateContext';
import { get } from '../../utils/api';
import { formatCurrencyDisplay } from '../portal/portalDonateShared';
import OutOfTownLocationPanel from '../../components/donate/OutOfTownLocationPanel';
import {
  mergedOutOfTownUi,
  normalizeOutOfTownDays,
  variationNeedsOutOfTownQuestion,
} from '../../utils/outOfTownSurcharge';

function isVariableProduct(product) {
  return String(product?.product_type || product?.type || '').toLowerCase() === 'variable';
}

const DEFAULT_DONATE_PRODUCT_IMAGE = '/assets/images/ICCT-Logo-trans.webp';

function productImageSrc(product) {
  const u = String(product?.image_url || product?.image || product?.images?.[0]?.src || '').trim();
  if (!u) return DEFAULT_DONATE_PRODUCT_IMAGE;
  if (/^https?:\/\//i.test(u)) return u;
  return u;
}

function onDonateProductImageError(e) {
  if (e?.currentTarget && e.currentTarget.src.indexOf(DEFAULT_DONATE_PRODUCT_IMAGE) === -1) {
    e.currentTarget.src = DEFAULT_DONATE_PRODUCT_IMAGE;
  }
}

function parseAttributes(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw;
  return null;
}

function prettify(val) {
  if (val == null) return 'Standard';
  const s = String(val).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function getVariationLabel(variation) {
  if (!variation) return 'Standard';
  const attrs = variation.attributes != null ? parseAttributes(variation.attributes) : null;
  if (Array.isArray(attrs) && attrs.length > 0) {
    const first = attrs[0];
    if (first && (first.option || first.name)) return prettify(first.option || first.name);
  }
  if (attrs && typeof attrs === 'object' && !Array.isArray(attrs)) {
    const values = Object.values(attrs).filter(Boolean);
    if (values.length > 0) {
      const v = values[0];
      if (v && typeof v === 'object' && (v.option || v.name)) return prettify(v.option || v.name);
      return prettify(v);
    }
  }
  const dn = String(variation.display_name || '').trim();
  if (dn) return dn;
  if (variation.name) return prettify(variation.name);
  return `Option #${variation.id}`;
}

function checkoutPathFromCart(cartPath) {
  const s = String(cartPath || '').replace(/\/$/, '');
  if (s.endsWith('/cart')) return `${s.slice(0, -'/cart'.length)}/checkout`;
  return `${s}/checkout`;
}

function isSimpleProductInCart(cart, productId) {
  return (
    Array.isArray(cart) &&
    cart.some(
      (ci) => Number(ci.product_id) === Number(productId) && Number(ci.variation_id || 0) === 0
    )
  );
}

/** Inline next-step actions (Option A) — only when this product is already in the cart. */
function DonateProductNextSteps({ cartPath }) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        mt: 1.5,
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: 2,
        bgcolor: '#fff7ed',
        border: '1px solid #fed7aa',
        boxShadow: '0 4px 14px rgba(180, 114, 24, 0.14)',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          component={NavLink}
          to={checkoutPathFromCart(cartPath)}
          variant="contained"
          color="warning"
          size="medium"
          sx={{ textTransform: 'none', fontWeight: 800, flex: { xs: 1, sm: 'none' } }}
        >
          Checkout
        </Button>
        <Button
          component={NavLink}
          to="/donate"
          variant="outlined"
          size="medium"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderWidth: 2,
            borderColor: '#c4b5a0',
            color: '#5a2d0c',
            flex: { xs: 1, sm: 'none' },
          }}
        >
          Continue shopping
        </Button>
      </Stack>
    </Box>
  );
}

export default function PublicDonateProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { loading: authLoading, staff } = useAuth();
  const { addItem, itemCount, donateMode, setGuestMode, cart, removeItem, updateOutOfTownDays } = usePublicDonate();
  const hasPortalSession = !authLoading && !!staff?.portal;
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [product, setProduct] = React.useState(null);
  const [variations, setVariations] = React.useState([]);
  const [variationQuantities, setVariationQuantities] = React.useState({});
  const [outOfTownUi, setOutOfTownUi] = React.useState(() => mergedOutOfTownUi());
  const [outOfTownByVariation, setOutOfTownByVariation] = React.useState({});
  const [expandedOutOfTownVariationId, setExpandedOutOfTownVariationId] = React.useState(null);
  const cartPath = '/donate/cart';

  const categorySlugs = React.useMemo(
    () => (Array.isArray(product?.category_slugs) ? product.category_slugs : []),
    [product]
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await get('/public/donate/fee-ui', {});
        if (!cancelled && r?.feeUi) setOutOfTownUi(mergedOutOfTownUi(r.outOfTown));
      } catch {
        /* defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!product?.id || !variations.length) return;
    const next = {};
    variations.forEach((variation) => {
      const inCart = cart.find(
        (ci) => Number(ci.product_id) === Number(product.id) && Number(ci.variation_id) === Number(variation.id)
      );
      next[variation.id] = normalizeOutOfTownDays(inCart?.out_of_town_days, outOfTownUi.maxDays);
    });
    setOutOfTownByVariation(next);
  }, [product?.id, variations, cart, outOfTownUi.maxDays]);

  useLayoutEffect(() => {
    if (authLoading) return;
    if (hasPortalSession) return;
    if (!donateMode) setGuestMode();
  }, [authLoading, hasPortalSession, donateMode, setGuestMode]);

  React.useEffect(() => {
    if (authLoading) return;
    if (!hasPortalSession && !donateMode) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await get('/public/facets/products', { slug });
        const row = Array.isArray(res?.products) ? res.products[0] : null;
        if (!row) {
          if (!cancelled) {
            navigate('/donate', { replace: true });
          }
          return;
        }
        let nextVariations = [];
        if (isVariableProduct(row)) {
          const vRes = await get('/public/facets/variations', { product_id: String(row.id), limit: 300 });
          nextVariations = Array.isArray(vRes?.variations) ? vRes.variations : [];
        }
        if (cancelled) return;
        const initialQty = {};
        nextVariations.forEach((variation) => {
          if (variation?.id != null) initialQty[variation.id] = 1;
        });
        setProduct(row);
        setVariations(nextVariations);
        setVariationQuantities(initialQty);
      } catch (e) {
        if (!cancelled) {
          navigate('/donate', { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, donateMode, hasPortalSession, navigate, slug]);

  const isVariationInCart = React.useCallback(
    (productId, variationId) =>
      Array.isArray(cart) &&
      cart.some(
        (ci) =>
          Number(ci.product_id) === Number(productId) &&
          Number(ci.variation_id || 0) === Number(variationId)
      ),
    [cart]
  );

  const handleSimpleProductAdd = React.useCallback(() => {
    if (!product) return;
    addItem({
      product_id: product.id,
      variation_id: 0,
      quantity: 1,
      product_name: product.name,
      variation_name: '',
      unit_price: Number(product?.price_num || 0),
    });
  }, [addItem, product]);

  const handleSimpleProductRemove = React.useCallback(() => {
    if (!product) return;
    removeItem(product.id, 0);
  }, [product, removeItem]);

  const handleVariationToggle = React.useCallback(
    (variation) => {
      if (!product?.id || !variation?.id) return;
      const inCart = isVariationInCart(product.id, variation.id);
      if (inCart) {
        removeItem(product.id, variation.id);
        setExpandedOutOfTownVariationId(null);
        return;
      }
      const qty = Math.max(1, parseInt(variationQuantities[variation.id], 10) || 1);
      const label = getVariationLabel(variation);
      const payload = {
        product_id: product.id,
        variation_id: variation.id,
        quantity: qty,
        product_name: product.name,
        variation_name: label,
        unit_price: Number(variation.price_num || 0),
      };
      if (variationNeedsOutOfTownQuestion(categorySlugs, label, product?.name)) {
        const days = normalizeOutOfTownDays(outOfTownByVariation[variation.id], outOfTownUi.maxDays);
        if (days > 0) payload.out_of_town_days = days;
      }
      addItem(payload);
      if (variationNeedsOutOfTownQuestion(categorySlugs, label, product?.name)) {
        setExpandedOutOfTownVariationId(variation.id);
      }
    },
    [
      addItem,
      categorySlugs,
      isVariationInCart,
      outOfTownByVariation,
      outOfTownUi.maxDays,
      product,
      removeItem,
      variationQuantities,
    ]
  );

  const handleOutOfTownChange = React.useCallback(
    (variation, days) => {
      const d = normalizeOutOfTownDays(days, outOfTownUi.maxDays);
      setOutOfTownByVariation((prev) => ({ ...prev, [variation.id]: d }));
      if (product?.id && isVariationInCart(product.id, variation.id)) {
        updateOutOfTownDays(product.id, variation.id, d);
      }
    },
    [isVariationInCart, outOfTownUi.maxDays, product?.id, updateOutOfTownDays]
  );

  if (authLoading || (!hasPortalSession && !donateMode)) {
    return (
      <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const img = productImageSrc(product);
  const variable = isVariableProduct(product);
  const simpleInCart = product ? isSimpleProductInCart(cart, product.id) : false;
  const anyVariationInCart =
    product && variable
      ? variations.some((v) => isVariationInCart(product.id, v.id))
      : false;

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="warning">{error}</Alert> : null}

      {!error && product ? (
        <Box sx={{ position: 'relative' }}>
          {itemCount > 0 ? (
            <Button
              component={NavLink}
              to={cartPath}
              variant="contained"
              color="secondary"
              startIcon={<ShoppingBagOutlinedIcon />}
              sx={{
                position: { xs: 'fixed', md: 'fixed' },
                right: { xs: 16, md: 24 },
                top: { xs: 'auto', md: '50%' },
                bottom: { xs: 18, md: 'auto' },
                transform: { xs: 'none', md: 'translateY(-50%)' },
                zIndex: 1200,
                borderRadius: 999,
                px: 2,
                py: 1.15,
                fontWeight: 800,
                boxShadow: '0 14px 32px rgba(190, 96, 0, 0.28)',
                bgcolor: '#ff6d00',
                color: '#fff',
                '&:hover': { bgcolor: '#e86400' },
              }}
            >
              Cart ({itemCount})
            </Button>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, md: 2.25 },
              pr: { xs: 1.5, md: itemCount > 0 ? 7 : 2.25 },
              borderRadius: 4,
              border: '1px solid',
              borderColor: '#e8dcc7',
              background: 'linear-gradient(135deg, #fffdfa 0%, #f7efe2 48%, #fff8ef 100%)',
              boxShadow: '0 20px 48px rgba(127, 86, 33, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '320px minmax(0, 1fr)' },
                gap: { xs: 1.5, md: 2.25 },
                alignItems: 'start',
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  overflow: 'hidden',
                  borderRadius: 3,
                  borderColor: '#eadbc5',
                  bgcolor: 'rgba(255,255,255,0.66)',
                  backdropFilter: 'blur(8px)',
                  position: { lg: 'sticky' },
                  top: { lg: 20 },
                }}
              >
                <Box
                  sx={{
                    aspectRatio: { xs: '16 / 10', sm: '4 / 3', lg: '1 / 1' },
                    bgcolor: '#f6eddc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    borderRadius: 3,
                    m: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={img}
                    alt={product.name}
                    onError={onDonateProductImageError}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      p: { xs: 1, md: 1.5 },
                    }}
                  />
                </Box>
              </Card>

              <Stack spacing={2}>
                <Box
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: '#eadbc5',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(255,249,239,0.92) 100%)',
                  }}
                >
                  <Stack spacing={1.25}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    justifyContent="space-between"
                    alignItems={{ md: 'flex-start' }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 800,
                            color: '#5a2d0c',
                            lineHeight: 1.06,
                            fontSize: { xs: '1.55rem', md: '2.1rem' },
                          }}
                        >
                          {product.name}
                        </Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ borderColor: '#ecd39d' }} />
                </Stack>
              </Box>

                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: '#eadbc5',
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
                    {variable ? (
                      <Stack spacing={1.5}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          Select an option
                        </Typography>
                      </Stack>
                        {variations.length === 0 ? (
                          <Alert severity="info">No options are currently available for this donation.</Alert>
                        ) : null}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'repeat(2, minmax(0, 1fr))' },
                            gap: 1.25,
                          }}
                        >
                          {variations.map((variation) => {
                            const inCart = isVariationInCart(product.id, variation.id);
                            const price = Number(variation.price_num || 0);
                            const isCheap = price <= 10;
                            const variationLabel = getVariationLabel(variation);
                            const needsOutOfTown = variationNeedsOutOfTownQuestion(
                              categorySlugs,
                              variationLabel,
                              product?.name
                            );
                            const showOutOfTownPanel =
                              needsOutOfTown &&
                              (inCart || Number(expandedOutOfTownVariationId) === Number(variation.id));
                            return (
                              <Box
                                key={variation.id}
                                onClick={() => {
                                  if (needsOutOfTown && !inCart) {
                                    setExpandedOutOfTownVariationId(variation.id);
                                  }
                                }}
                                sx={{
                                  p: 1.2,
                                  border: inCart || showOutOfTownPanel ? '2px solid' : '1px solid',
                                  borderColor: inCart || showOutOfTownPanel ? '#ff8a1f' : '#e9ddca',
                                  borderRadius: 2.5,
                                  bgcolor: inCart || showOutOfTownPanel ? '#fff1db' : '#fffdfa',
                                  cursor: needsOutOfTown && !inCart ? 'pointer' : undefined,
                                  boxShadow: inCart ? '0 18px 34px rgba(180, 114, 24, 0.22)' : '0 8px 18px rgba(124, 93, 51, 0.06)',
                                  position: 'relative',
                                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: inCart ? '0 18px 34px rgba(180, 114, 24, 0.22)' : '0 14px 28px rgba(124, 93, 51, 0.1)',
                                  },
                                }}
                              >
                                {inCart ? (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 10,
                                      right: 10,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      px: 1,
                                      py: 0.45,
                                      borderRadius: 999,
                                      bgcolor: '#ffb55a',
                                      color: '#5f3200',
                                      border: '1px solid #ff9829',
                                      boxShadow: '0 6px 14px rgba(180, 114, 24, 0.18)',
                                    }}
                                  >
                                    <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                      Selected
                                    </Typography>
                                  </Box>
                                ) : null}
                                <Stack
                                  direction={{ xs: 'column', sm: 'row' }}
                                  spacing={1}
                                  justifyContent="space-between"
                                  alignItems={{ xs: 'stretch', sm: 'center' }}
                                >
                                  <Box sx={{ minWidth: 0, flex: 1, pr: { sm: 1 } }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2, pr: inCart ? 10 : 0 }}>
                                      {variationLabel}
                                    </Typography>
                                    {Number(variation.price_num || 0) === 1 ? (
                                      <Typography variant="caption" sx={{ color: '#9a5500', display: 'block', mt: 0.35, fontWeight: 600 }}>
                                        Change quantity to the dollar amount you want to donate.
                                      </Typography>
                                    ) : null}
                                    {showOutOfTownPanel ? (
                                      <Box onClick={(e) => e.stopPropagation()}>
                                        <OutOfTownLocationPanel
                                          value={outOfTownByVariation[variation.id] ?? 0}
                                          onChange={(days) => handleOutOfTownChange(variation, days)}
                                          dayRate={outOfTownUi.dayRate}
                                          maxDays={outOfTownUi.maxDays}
                                        />
                                      </Box>
                                    ) : null}
                                  </Box>
                                  <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                    justifyContent="flex-end"
                                    sx={{ flexShrink: 0 }}
                                  >
                                    {isCheap ? (
                                      <Box
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.35,
                                          border: '1px solid #eadbc5',
                                          borderRadius: 999,
                                          px: 0.35,
                                          py: 0.2,
                                          bgcolor: '#fff',
                                          alignSelf: { xs: 'flex-start', sm: 'center' },
                                        }}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setVariationQuantities((prev) => ({
                                              ...prev,
                                              [variation.id]: Math.max(1, (parseInt(prev[variation.id], 10) || 1) - 1),
                                            }));
                                          }}
                                          disabled={(variationQuantities[variation.id] || 1) <= 1}
                                        >
                                          <RemoveIcon fontSize="small" />
                                        </IconButton>
                                        <TextField
                                          type="number"
                                          size="small"
                                          value={variationQuantities[variation.id] || 1}
                                          onChange={(e) =>
                                            setVariationQuantities((prev) => ({
                                              ...prev,
                                              [variation.id]: Math.max(1, parseInt(e.target.value, 10) || 1),
                                            }))
                                          }
                                          inputProps={{ min: 1, style: { textAlign: 'center', width: 42, padding: '8px 0' } }}
                                          sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setVariationQuantities((prev) => ({
                                              ...prev,
                                              [variation.id]: (parseInt(prev[variation.id], 10) || 1) + 1,
                                            }));
                                          }}
                                        >
                                          <AddIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    ) : null}
                                    <Box
                                      sx={{
                                        px: 1,
                                        py: 0.6,
                                        borderRadius: 999,
                                        bgcolor: '#fff5e5',
                                        border: '1px solid #f2d6a6',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#9a5500' }}>
                                        {formatCurrency(variation.price_num)}
                                      </Typography>
                                    </Box>
                                    {inCart ? (
                                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          startIcon={<DeleteOutlineIcon />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleVariationToggle(variation);
                                          }}
                                          sx={{
                                            fontWeight: 800,
                                            borderRadius: 999,
                                            px: 2.2,
                                            whiteSpace: 'nowrap',
                                            borderWidth: 2,
                                            bgcolor: '#fff',
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      </Stack>
                                    ) : (
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleVariationToggle(variation);
                                        }}
                                        sx={{
                                          fontWeight: 800,
                                          borderRadius: 999,
                                          px: 2.2,
                                          whiteSpace: 'nowrap',
                                          background: 'linear-gradient(135deg, #1a237e 0%, #3141b7 100%)',
                                        }}
                                      >
                                        Add to cart
                                      </Button>
                                    )}
                                  </Stack>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Box>
                        {anyVariationInCart ? (
                          <DonateProductNextSteps cartPath={cartPath} />
                        ) : null}
                      </Stack>
                    ) : (
                      <Stack spacing={1.5}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Add this donation
                        </Typography>
                        {Number(product?.price_num || 0) > 0 ? (
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 800, color: '#5a2d0c', lineHeight: 1.1 }}
                          >
                            {formatCurrency(product.price_num)}
                          </Typography>
                        ) : null}
                        {simpleInCart ? (
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={handleSimpleProductRemove}
                            sx={{ alignSelf: 'flex-start', fontWeight: 800, borderWidth: 2 }}
                          >
                            Remove from cart
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSimpleProductAdd}
                            sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
                          >
                            Add to cart
                          </Button>
                        )}
                        {simpleInCart ? (
                          <DonateProductNextSteps cartPath={cartPath} />
                        ) : null}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            </Box>
          </Paper>
        </Box>
      ) : null}
    </Stack>
  );
}
